import * as React from 'react';

import {useSetHighlight} from '../../state/interactState/hooks';

let timer: ReturnType<typeof setTimeout> | undefined;

const useUnselectActiveRun = (
  axis: string = 'run:name',
  delay: number = 200
) => {
  const setActive = useSetHighlight();

  const handleMouseOut = React.useCallback(() => {
    timer = setTimeout(() => {
      setActive(axis, undefined);
    }, delay);
  }, [axis, setActive, delay]);

  const handleMouseIn = React.useCallback(
    (activeRun: string) => {
      setActive(axis, activeRun);
      if (timer) {
        clearTimeout(timer);
      }
    },
    [axis, setActive]
  );

  return React.useMemo(
    () => ({
      handleMouseIn,
      handleMouseOut,
    }),
    [handleMouseIn, handleMouseOut]
  );
};

const hardCodedStyles = {
  flex: '1 1 auto',
  overflow: 'hidden',
};

/**
 * This is a wrapper component for the Runs in the RunSelector. Because we can't wrap the items at the level we need to in WBReactTable, this wraps each individual item and uses a timer that can be cleared in order to allow a user to mouse from one run to another through the deadspace between the run names without blinking the background dimming. When a user mouses out of a run without mousing into a new one, the timer will fire shortly after to clear the dimming effect.
 */
export const RunSelectorInteractTracker = React.memo(
  ({
    axis,
    children,
    className = '',
    delay,
    runSelector,
  }: {
    axis?: string;
    className: string;
    children: React.ReactNode;
    delay?: number;
    runSelector: string;
  }) => {
    const {handleMouseIn, handleMouseOut} = useUnselectActiveRun(axis, delay);

    const handleIn = React.useCallback(() => {
      handleMouseIn(runSelector);
    }, [runSelector, handleMouseIn]);

    const handleOut = React.useCallback(() => {
      handleMouseOut();
    }, [handleMouseOut]);

    return (
      <div
        className={className}
        key={runSelector}
        onMouseOver={handleIn}
        onMouseOut={handleOut}
        style={hardCodedStyles}>
        {children}
      </div>
    );
  }
);

RunSelectorInteractTracker.displayName = 'RunSelectorInteractTracker';
